import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  toast,
} from "@devhop/ui";
import { useState } from "react";
import { useCreateAdminEventMutation } from "@/modules/events/presentation/api/queries";

const SLUG_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (eventId: string) => void;
};

export function CreateEventDialog({ open, onOpenChange, onCreated }: Props) {
  const createMutation = useCreateAdminEventMutation();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [venue, setVenue] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [judge1Pin, setJudge1Pin] = useState("");
  const [judge2Pin, setJudge2Pin] = useState("");
  const [judge3Pin, setJudge3Pin] = useState("");

  const reset = () => {
    setName("");
    setSlug("");
    setVenue("");
    setEventDate("");
    setJudge1Pin("");
    setJudge2Pin("");
    setJudge3Pin("");
  };

  const handleClose = (next: boolean) => {
    if (!next && !createMutation.isPending) reset();
    onOpenChange(next);
  };

  const handleSubmit = async () => {
    const n = name.trim();
    const s = slug.trim().toLowerCase();
    if (!n || !s) {
      toast.error("ກະລຸນາໃສ່ຊື່ ແລະ slug");
      return;
    }
    if (!SLUG_REGEX.test(s)) {
      toast.error("slug ຕ້ອງເປັນ a-z ຕົວເລກຂີດກັນຂກ (ຕົວຢ່າງ: my-show-2026)");
      return;
    }
    if (!judge1Pin.trim() || !judge2Pin.trim() || !judge3Pin.trim()) {
      toast.error("ກະລຸນາເຕືມ PIN ກຳມະການທັງສາມຊ່ອງ");
      return;
    }
    try {
      const ev = await createMutation.mutateAsync({
        name: n,
        slug: s,
        venue: venue.trim() || undefined,
        eventDate: eventDate.trim() ? eventDate.trim() : null,
        judge1Pin: judge1Pin.trim(),
        judge2Pin: judge2Pin.trim(),
        judge3Pin: judge3Pin.trim(),
      });
      reset();
      onOpenChange(false);
      onCreated?.(ev.id);
    } catch {
      /* toast in mutation */
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>ສ້າງງານແຂ່ງຂັນໃໝ່</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3 py-1">
          <div className="flex flex-col gap-2">
            <Label htmlFor="ce-name">ຊື່ງານ</Label>
            <Input
              id="ce-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="ງານຮຽກຮ້ອງ ຄັບເວີ ແດນສະຕອບ 2026"
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="ce-slug">Slug (ສະເພາະເວັບ)</Label>
            <Input
              id="ce-slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase())}
              placeholder="kids-expo-2026-cover"
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="ce-venue">ສະຖານທີ່</Label>
            <Input
              id="ce-venue"
              value={venue}
              onChange={(e) => setVenue(e.target.value)}
              placeholder="ຫ້ອງເຊທຟໍງານສະແດງ..."
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="ce-date">ວັນທີ</Label>
            <Input
              id="ce-date"
              type="date"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
            />
          </div>

          <p className="mt-2 font-medium text-foreground text-sm">
            ລະຫັດເຂົ້າກຳມະການ (ງານລະນີ້)
          </p>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="flex flex-col gap-2">
              <Label htmlFor="ce-j1">ກຳມະການ 1</Label>
              <Input
                id="ce-j1"
                type="password"
                autoComplete="new-password"
                value={judge1Pin}
                onChange={(e) => setJudge1Pin(e.target.value)}
                placeholder="••••"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="ce-j2">ກຳມະການ 2</Label>
              <Input
                id="ce-j2"
                type="password"
                autoComplete="new-password"
                value={judge2Pin}
                onChange={(e) => setJudge2Pin(e.target.value)}
                placeholder="••••"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="ce-j3">ກຳມະການ 3</Label>
              <Input
                id="ce-j3"
                type="password"
                autoComplete="new-password"
                value={judge3Pin}
                onChange={(e) => setJudge3Pin(e.target.value)}
                placeholder="••••"
              />
            </div>
          </div>
          <p className="text-muted-foreground text-xs">
            Admin ເຂົ້າລະບົບດ້ວຍ ADMIN_PIN ຈາກລະບົບ (ເຫຼົ່າເດີມໃນການຕັ້ງຄ່າ).
          </p>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => handleClose(false)}
            disabled={createMutation.isPending}
          >
            ຍົກເລີກ
          </Button>
          <Button onClick={handleSubmit} disabled={createMutation.isPending}>
            {createMutation.isPending ? "ກຳລັງບັນທຶກ..." : "ສ້າງງານ"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
